import React, { Component } from 'react';
import { DataBox } from '../../common/components/data-box.component';
import { Analytics } from '../../analytics/components/analytics.container';
import { Loading } from '../../common/components/loading.component';
import { Theme } from '../../theme/theme';
import moment from 'moment';

function rateColor(rate) {
  if (rate >= 70) return '#16a34a';
  if (rate >= 50) return '#d97706';
  return '#dc2626';
}

const StatTile = ({ label, value, sublabel }) => (
  <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-4 text-center">
    <div className="text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
    <div className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    {sublabel && <div className="mt-0.5 text-[10px] text-gray-400">{sublabel}</div>}
  </div>
);

const VideoPerformanceRow = ({ item }) => (
  <div
    className="rounded-md border border-gray-100 bg-gray-50 p-3"
    style={{ borderLeftWidth: 3, borderLeftColor: item.color }}
  >
    <div className="mb-2 text-sm font-semibold text-gray-800">{item.name}</div>
    <div className="grid grid-cols-3 gap-2 text-sm">
      <div>
        <div className="text-xs text-gray-500">Views</div>
        <div className="font-medium tabular-nums text-gray-900">{item.views}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Completions</div>
        <div className="font-medium tabular-nums text-gray-900">{item.completions}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Rate</div>
        <div className="font-semibold tabular-nums" style={{ color: rateColor(item.rate) }}>
          {item.rate}%
        </div>
      </div>
    </div>
  </div>
);

export class DashboardView extends Component {
  constructor() {
    super();
    this.state = {
      startTime: moment().subtract(7, 'days').startOf('day'),
      endTime: moment().endOf('day'),
    };
  }

  handleDateChange = (startTime, endTime) => {
    this.setState({ startTime, endTime }, () => {
      if (this.props.data && this.props.data.refetch) {
        this.props.data.refetch({
          startTime: startTime.utc().format(),
          endTime: endTime.utc().format(),
        });
      }
    });
  };

  getAnalyticsParams() {
    return [
      { category: 'App', action: 'AppVisit', identifier: null, name: 'App Uses' },
      { category: 'App', action: 'AppVisit', identifier: null, name: 'Unique App Uses', flags: { unique: true } },
      { category: 'Auth', action: 'Login', identifier: null, name: 'User Logins' },
      { category: 'Auth', action: 'Login', identifier: null, name: 'Unique Logins', flags: { unique: true } },
      { category: 'Video', action: 'VideoSelect', identifier: null, name: 'Video Selections' },
      { category: 'Video', action: 'VideoStart', identifier: null, name: 'Video Starts' },
      { category: 'Video', action: 'VideoComplete', identifier: null, name: 'Video Completions' },
      { category: 'Manual', action: 'ManualView', identifier: null, name: 'Manual Views' },
      { category: 'Homepage', action: 'PostCreate', identifier: null, name: 'Posts Created' },
      { category: 'Homepage', action: 'PostClick', identifier: null, name: 'Post Clicks' },
      { category: 'Homepage', action: 'PostReaction', identifier: null, name: 'Post Reactions' },
      { category: 'Homepage', action: 'PostComment', identifier: null, name: 'Post Comments' },
      { category: 'Homepage', action: 'CommentLike', identifier: null, name: 'Comment Likes' },
      //{ category: 'Feed-EventView-NewsFeedEventItem', action: 'EventView', identifier: null, name: 'Event Views' },
     // { category: 'Feed-EventView-NewsFeedItemType', action: 'EventView', identifier: null, name: 'Sports Event Views' },
      // { category: 'Feed-NewsReadMore-NewsFeedNewsItem', action: 'NewsReadMore', identifier: null, name: 'News Read More' },
      { category: 'Homepage', action: 'NewsReadMore', identifier: null, name: 'News Read More' },
      // { category: 'Feed', action: 'AnswerPoll', identifier: null, name: 'Poll Votes' },
      // { category: 'Feed', action: 'ToggleReminder', identifier: null, name: 'Reminder Toggles' },
      // { category: 'Feed', action: 'ExportEvent', identifier: null, name: 'Event Exports' },
      // { category: 'Feed', action: 'RSVP', identifier: null, name: 'RSVP Actions' },
      // { category: 'Feed', action: 'FeedPosterClick', identifier: null, name: 'Poster Clicks' },
      //{ category: 'Feature', action: 'Yoga Sessions', identifier: null, name: 'Yoga Sessions' },
      //{ category: 'Feature', action: 'Yoga Poses', identifier: null, name: 'Yoga Poses' },
      //{ category: 'Feature', action: 'Yoga Modifications', identifier: null, name: 'Yoga Modifications' },
      //{ category: 'Feature', action: 'Yoga Science', identifier: null, name: 'Yoga Science' },
    ];
  }

  getVideoAnalyticsByType(statisticsData) {
    return [
      {
        name: 'Sessions',
        views: statisticsData?.sessionVideoViews || 0,
        completions: statisticsData?.sessionVideoCompletions || 0,
        rate: statisticsData?.sessionVideoCompletionRate || 0,
        color: Theme.PRIMARY_COLOR,
      },
      {
        name: 'Poses',
        views: statisticsData?.poseVideoViews || 0,
        completions: statisticsData?.poseVideoCompletions || 0,
        rate: statisticsData?.poseVideoCompletionRate || 0,
        color: Theme.ACTIVE1_COLOR,
      },
      {
        name: 'Modifications',
        views: statisticsData?.modificationVideoViews || 0,
        completions: statisticsData?.modificationVideoCompletions || 0,
        rate: statisticsData?.modificationVideoCompletionRate || 0,
        color: Theme.ACTIVE2_COLOR,
      },
    ];
  }

  getVideoAnalyticsByDuration(statisticsData) {
    return [
      {
        name: 'Short Sessions',
        views: statisticsData?.shortSessionViews || 0,
        completions: statisticsData?.shortSessionCompletions || 0,
        rate: statisticsData?.shortSessionCompletionRate || 0,
        color: Theme.PRIMARY_COLOR,
      },
      {
        name: 'Medium Sessions',
        views: statisticsData?.mediumSessionViews || 0,
        completions: statisticsData?.mediumSessionCompletions || 0,
        rate: statisticsData?.mediumSessionCompletionRate || 0,
        color: Theme.ACTIVE1_COLOR,
      },
      {
        name: 'Long Sessions',
        views: statisticsData?.longSessionViews || 0,
        completions: statisticsData?.longSessionCompletions || 0,
        rate: statisticsData?.longSessionCompletionRate || 0,
        color: Theme.ACTIVE2_COLOR,
      },
    ];
  }

  render() {
    const { data, client, statisticsData } = this.props;
    const d = statisticsData || {};

    if (data && data.loading) {
      return <Loading />;
    }

    const completionRate = d.totalVideoViews
      ? Math.round((d.completedVideoViews / d.totalVideoViews) * 100)
      : 0;

    return (
      <div className="mx-auto max-w-[1400px] space-y-6 px-6 pb-8 pt-2">
        <header className="border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Platform overview and engagement for the selected date range
          </p>
        </header>

        <section>
          <DataBox title="Platform">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatTile label="Users" value={d.users || 0} />
              <StatTile label="Videos" value={d.totalVideos || 0} />
              <StatTile label="Manuals" value={d.totalManuals || 0} />
              <StatTile label="Posts" value={d.totalPosts || 0} />
              <StatTile label="Comments" value={d.totalComments || 0} />
            </div>
          </DataBox>
        </section>

        <section>
          <DataBox title="App Use Analytics">
            <Analytics
              params={this.getAnalyticsParams()}
              client={client}
              startTime={this.state.startTime}
              endTime={this.state.endTime}
              onDateChange={this.handleDateChange}
            />
          </DataBox>
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DataBox title="Video Library">
            <div className="grid grid-cols-3 gap-3">
              <StatTile label="Sessions" value={d.sessionVideos || 0} />
              <StatTile label="Poses" value={d.poseVideos || 0} />
              <StatTile label="Modifications" value={d.modificationVideos || 0} />
            </div>
          </DataBox>

          <DataBox title="Video Performance">
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <StatTile label="Views" value={d.totalVideoViews || 0} sublabel="in period" />
                <StatTile label="Completions" value={d.completedVideoViews || 0} sublabel="in period" />
                <StatTile
                  label="Completion Rate"
                  value={`${completionRate}%`}
                  sublabel="in period"
                />
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  By type
                </p>
                <div className="space-y-2">
                  {this.getVideoAnalyticsByType(d).map((item, i) => (
                    <VideoPerformanceRow key={i} item={item} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  By session length
                </p>
                <div className="space-y-2">
                  {this.getVideoAnalyticsByDuration(d).map((item, i) => (
                    <VideoPerformanceRow key={i} item={item} />
                  ))}
                </div>
              </div>
            </div>
          </DataBox>
        </section>
      </div>
    );
  }
}

export const Dashboard = DashboardView;
